import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInterval } from "hooks/useInterval";
import Heading from "components/Heading";
import Button from "components/Button";
import { UploadIcon } from "@heroicons/react/solid";
import SearchSortTable from "components/Table";
import UploadData from "features/Building/BuildingSourceFiles/UploadData";
import useCreateSourceFilesTableColumns from "./useCreateSourceFilesTableColumns";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { ModalQualityPopup } from "./ModelQualityPopup";
import { observer } from "mobx-react-lite";
import { skeleton } from "utils";
import { buildingStore } from "store/Building/BuildingStore";
import { Report } from "features/MappingEditor/utils";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";
import Tooltip from "components/Tooltip";

const BuildingSourceFiles = observer(() => {
  const { t } = useTranslation();
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const { sourceFiles } = buildingStore;
  const [openModelQualityPopup, setOpenModelQualityPopup] =
    React.useState(false);
  const [report, setReport] = React.useState<Report | null>(null);
  const { hasFeature } = useFeatureAccess();

  useEffect(() => {
    if (!getSortingState().length)
      saveSortingState([{ id: "Upload date", desc: true }]);
    (async () => await buildingStore.getAllSourceFiles())();
  }, []);

  useInterval(async () => {
    await buildingStore.getAllSourceFiles();
  }, 30000);

  const handleOpenFileUpload = () => {
    setShowFileUpload(true);
  };

  const checkLoadingState = () =>
    sourceFiles.loading ? (
      skeleton("h-80")
    ) : (
      <div className="border rounded-lg border-gray-300 p-2 bg-white">
        <UploadData open={showFileUpload} setClose={setShowFileUpload} />
      </div>
    );

  const saveSortingState = (value: SortingState) =>
    localStorage.setItem("material_resources_sort", JSON.stringify(value));

  const getSortingState = () =>
    JSON.parse(localStorage.getItem("material_resources_sort") ?? "[]");

  const getColumns = useCallback(() => {
    return useCreateSourceFilesTableColumns(
      setOpenModelQualityPopup,
      setReport
    );
  }, []);

  const hasUploadLicense = () => {
    return hasFeature(LicenseFeatureEnum.BuildingDataUploadIfcCsv);
  };

  return (
    <div id="IFC-File-list-view" className="p-6">
      <div className="flex justify-between pb-2">
        <Heading size={"title"}>{t("buildingMaterialResources.title")}</Heading>
        {sourceFiles.items.length > 0 && (
          <Tooltip
            content={
              hasUploadLicense()
                ? ""
                : t("buildingMaterialResources.uploadDisabledTooltip")
            }
            tooltipClassName="!w-[320px]"
          >
            <Button
              type="primary"
              width="fit-content"
              leadingIcon={<UploadIcon />}
              onClick={handleOpenFileUpload}
              testID="uploaddata_button"
              disable={!hasUploadLicense()}
            >
              {t("buildingMaterialResources.uploadData")}
            </Button>
          </Tooltip>
        )}
      </div>
      <div className="pb-4 text-sm text-gray-700">
        {t("buildingMaterialResources.uploadFileMessage")}
      </div>
      <div className="pt-2">
        {sourceFiles.items.length ? (
          <>
            <ModalQualityPopup
              open={openModelQualityPopup}
              close={() => setOpenModelQualityPopup(false)}
              report={report}
            />
            <SearchSortTable
              getColumns={getColumns as unknown as () => ColumnDef<unknown>[]}
              data={sourceFiles.items}
              customContainerStyle="overflow-visible"
              borderedStyle
              scrollDivClassName="mt-3 scrollbar"
              onSortChanged={saveSortingState}
              defaultSort={getSortingState()}
            />
          </>
        ) : (
          checkLoadingState()
        )}
      </div>
      {showFileUpload && (
        <UploadData open={showFileUpload} setClose={setShowFileUpload} modal />
      )}
    </div>
  );
});

export default BuildingSourceFiles;
